import { useEffect, useRef, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useCurrentUser } from "@/auth/RequireAuth";
import { start, stop, track, type AnalyticsConfig } from "./client";
import { routeTemplate } from "./route";
import { buildTag, describeUntagged } from "./tag";

const envConfig: AnalyticsConfig = {
  key: import.meta.env.VITE_POSTHOG_KEY,
  host: import.meta.env.VITE_POSTHOG_HOST,
};

/**
 * Product analytics for the signed-in workspace (spec D2). Starts PostHog for
 * the current user, sends `ui_element_clicked` for every click on a `data-ph`
 * element (`tag_source: "explicit"`) or on an untagged interactive element
 * (`tag_source: "untagged"`, role and scope only, so coverage gaps show; spec
 * T3), and one `$pageview` per route template. Clicks on anything else send
 * nothing. Everything is a no-op when no key is configured.
 */
export function AnalyticsProvider({
  children,
  config = envConfig,
}: {
  children: ReactNode;
  config?: AnalyticsConfig;
}) {
  const user = useCurrentUser();
  const route = routeTemplate(useLocation().pathname);
  const routeRef = useRef(route);
  routeRef.current = route;

  useEffect(() => {
    if (!user) {
      stop();
      return;
    }
    void start(config, user);
    return () => stop();
    // Restart only when the identity changes, not on every new user object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.company_id, config.key, config.host]);

  useEffect(() => {
    // Capture phase: a handler that stops propagation can't hide the click.
    // Fails safe: analytics must never break a click, so a failure is logged, not thrown.
    const onClick = (e: MouseEvent) => {
      try {
        if (!(e.target instanceof Element)) return;
        const payload = buildTag(e.target) ?? describeUntagged(e.target);
        if (payload) track("ui_element_clicked", { ...payload, route: routeRef.current });
      } catch (err) {
        console.warn("[analytics] click tracking failed", err);
      }
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  useEffect(() => {
    track("$pageview", { route });
  }, [route]);

  return <>{children}</>;
}
